/**
 *  @file       tmpfiles.js            Utilities for securely and portably creating temporary
 *                                     files with NodeJS.
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       April 2020
 */
'use strict';
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const path = requireNative('path');
const fs = requireNative('fs');
const os = requireNative('os');
const process = requireNative('process');
const { assert } = require('dcp/common/dcp-assert');
const { atExit, atExitCancel } = require('./index');
const debugging = require('dcp/debugging').scope('tmpfiles');

/**
 * deterministic initializer ensures dcpConfig is ready
 */
exports.getSystemTempDir = function tmpfiles$$getSystemTempDir()
{
  if (!exports.hasOwnProperty('_systemTempDir'))
  {
    let systemTempDir = process.env.TMPDIR || (dcpConfig.system && dcpConfig.system.tempDir) || (process.platform === 'win32' && process.env.TEMP);
    if (!systemTempDir) {
      if (process.platform === 'win32') {
        if (process.env.LOCALAPPDATA) 
          systemTempDir = path.join(systemTempDir, 'Temp');
        else if (process.env.HOMEDRIVE && process.env.HOMEPATH)
          systemTempDir = path.join(process.env.HOMEDRIVE + process.env.HOMEPATH, 'AppData', 'Local', 'Temp');
        else if (process.env.SystemRoot)
          systemTempDir = path.join(process.env.SystemRoot, 'Temp');
        else if (process.env.SystemDrive)
          systemTempDir = path.join(process.env.SystemDrive + '\\Windows', 'Temp');
        else
          systemTempDir = 'C:\\WINDOWS\\Temp'
      }
      else
        systemTempDir = '/tmp';
    }

    exports._systemTempDir = systemTempDir;
  }

  debugging() && console.debug('tmp - system temp dir is', exports._systemTempDir);
  return exports._systemTempDir;
}

/**
 * Create a temporary thing in the filesystem.
 *
 * @param   {function}  createThingFn           The function which creates the thing; must take as its argument
 *                                              a fully-pathed filename and throw an exception failure. It must
 *                                              return a handle to the thing, which will become the return value
 *                                              of this function.
 * @param   {string}    basenamePattern         A string specifying the file basename pattern. The
 *                                              character X will be replaced with randomly-generated
 *                                              characters, similar to mkstemp(3).
 * @param   {string}    extension [optional]    The filename extension to use; false => none, falsey => dcp
 * @param   {string}    tempDir   [optional]    The directory in which to create the thing. Default is
 *                                              exports.systemTempDir.
 *
 * @return      a handle created by createThingFn, which contains removeFn(), a function which can remove all
 *              traces of the temporary thing.
 *
 */
exports.createTempThing = function createTempThing(createThingFn, basenamePattern, extension, tempDir)
{
  const generateEntropy = require('nanoid').customAlphabet('1234567890abcdefghijklmnopqrstuvwyxzkABCDEFGHIJKLMNOPQRSTUVWXYZ', 64);

  var entropy = generateEntropy();
  var basename = '';
  var filename;                 /* The filename of the temp thing we are creating */
  var thing;                    /* The temp thing we are creating */
  var removed = false;          /* Used by removeFn to avoid races */

  if (!tempDir)
    tempDir = exports.getSystemTempDir();
  
  if (!basenamePattern)
    basenamePattern = `dcp-${process.pid}-XXXXXXXX`;

  const re = /[xX]{3,}/;
  assert(re.test(basenamePattern))

  let tries = 0 
  loop: while ('goto considered harmful considered harmful') { // eslint-disable-line no-constant-condition
    let j = Math.floor(Math.random() * entropy.length);
    for (let i=0; i < basenamePattern.length; i++) {
      if (basenamePattern[i] === 'X')
        basename += entropy[j++ % entropy.length];
      else
        basename += basenamePattern[i];
    }

    if (extension !== false)
      basename += '.' + (extension || 'dcp');
    filename = path.join(tempDir, basename);
    if (fs.existsSync(filename)) {
      tries = tries++; 
      basename='';
      if (tries > 1000)
        throw new Error('DCPU-1001'); /* this is ~impossible */
      continue loop;
    }
    try {
      debugging() && console.debug('tmp - creating temp thing', filename, 'tries=', tries);
      thing = createThingFn(filename);
    } catch(e) {
      if (e.code === 'EEXIST')
        continue;
      throw e;
    }
    break loop;
  }

  function removeFn()
  {
    require('dcp/node-libs/shutdown').atExitCancel(removeFn);
    if (removed)
      return;
    if (!process.env.DCP_DEBUG_TMP_FILES)
    {
      try
      {
        try
        {
          debugging() && console.debug('tmp - unlinking', filename);
          fs.unlinkSync(filename);
          removed = true;
        }
        catch(error)
        {
          if (!fs.lstatSync(filename).isDirectory())
            throw error;
          fs.rmSync(filename, { recursive: true, maxRetries: 20, force: true });
          removed = true;
        }
      }
      catch(error)
      {
        if (error.code !== 'ENOENT')
          process.emitWarning(`Warning: unable to clean up '${filename}'`, error.message);
        else
          removed = true;
      }
    }
  }
  
  return { thing, removeFn, filename };
}

/** 
 * Securely create a temporary file in the system temp directory.  A cleanup is registered to
 * automatically remove the file when the process is closed.
 * 
 * @param    {string} basenamePattern   A string specifying the file basename pattern. The
 *                                      character X will be replaced with randomly-generated
 *                                      characters, similar to mkstemp(3).
 * @param    {string} extension         [optional]  The filename extension to use; pass false for none
 * @param             perms             [optional]  The permissions argument to openSync.
 * @param    {string} tempDir           [optional]  The directory in which to create the file. Default
 *                                                  is exports.systemTempDir.
 *
 * @returns  {object} An object with properties:
 *                    fd:        the open file descriptor
 *                    filename:  the name of the newly-created file
 *                    toString:  method which returns the filename
 *                    write:     method which writes to the file descriptor (like fs.write)
 *                    writeSync: method which writes to the file descriptor (like fs.writeSync)
 *                    close:     method which closes the file descriptor (like fs.close)
 *                    closeSync: method which closes the file descriptor (like fs.closeSync)
 *                    chmod:     method which changes the file mode (like fs.fchmod)
 *                    chmodSync: method which changes the file mode (like fs.fchmodSync)
 *                    remove:    method which remove the file from disk and cleans up the registered
 *                               process.exit cleanup. Does not close the file descriptor!
 */
exports.createTempFile = function utils$$createTempFile(basenamePattern, extension, perms, tempDir)
{
  const inspect  = requireNative('util').inspect.custom;
  let f = fs.constants;
  let hnd;

  /** Atomically create a file using the permissions in the closed-over environment.
   *  Atomic here means that either the file will be created or it won't be, and the
   *  check-then-create race condition is handled by the OS.
   */
  function atomicCreateFile(filename) {
    const mode = perms | 0o600;
    debugging() && console.debug('tmp - creat', filename, mode.toString(8));
    return fs.openSync(filename, f.O_RDWR | f.O_CREAT | f.O_EXCL, mode);
  }

  const { thing: fd, removeFn, filename } = exports.createTempThing(atomicCreateFile, basenamePattern, extension, tempDir);

  hnd = { fd, filename };
  hnd.toString = () => filename;
  hnd.write     = function tmp$write()     { return fs.write     .apply(null, [fd].concat(Array.from(arguments))) };
  hnd.writeSync = function tmp$writeSync() { return fs.writeSync .apply(null, [fd].concat(Array.from(arguments))) };
  hnd.chmod     = function tmp$chmod()     { return fs.fchmod    .apply(null, [fd].concat(Array.from(arguments))) };
  hnd.chmodSync = function tmp$chmodSync() { return fs.fchmodSync.apply(null, [fd].concat(Array.from(arguments))) };
  hnd.close     = function tmp$close()     {        fs.close     .apply(null, [fd].concat(Array.from(arguments))); delete hnd.fd; return hnd };
  hnd.closeSync = function tmp$closeSync() {        fs.closeSync .apply(null, [fd].concat(Array.from(arguments))); delete hnd.fd; return hnd };
  hnd.remove    = removeFn;

  hnd[inspect] = () => '[Object utils$$tempFile(' + filename + ')]';
   
  require('dcp/node-libs/shutdown').atExit(removeFn);
  return hnd;
}

/**
 * Securely create a temporary directory in the system temp directory. A cleanup is registered to
 * automatically remove the directory and its children when the process is closed.
 *
 * @param    {string} basenamePattern   A string specifying the file basename pattern. The
 *                                      character X will be replaced with randomly-generated
 *                                      characters, similar to mkstemp(3).
 * @param    {string} extension         [optional]  The filename extension to use; pass false for none
 * @param             perms             [optional]  The mode argument to mkdirSync
 * @param   {string}  tempDir           [optional]  The directory in which to create the directory.
 *
 * @returns  {object} An object with properties:
 *                    fd:        the open file descriptor
 *                    dirname:  the name of the newly-created directory
 *                    toString:  method which returns dirname
 *                    remove:    method which removes the directory and its children
 *                               from disk and cleans up the registered process.exit cleanup.
 */
exports.createTempDir = function utils$$createTempDir(basenamePattern, extension, perms, tempDir)
{
  const inspect  = requireNative('util').inspect.custom;

  function createDir(dirname)
  {
    var opts = { recursive: Boolean(tempDir) };

    if (typeof perms !== 'undefined')
      opts.mode = perms;

    debugging() && console.debug('tmp - mkdir', dirname, opts?.mode?.toString(8) || '');
    fs.mkdirSync(dirname, opts);
  }
  
  const { thing: fd, removeFn, filename } = exports.createTempThing(createDir, basenamePattern, extension, tempDir);
  const hnd = { fd, dirname: filename };
  hnd.toString = () => filename;
  hnd.remove   = removeFn;
  hnd[inspect] = () => '[Object utils$$tempDir(' + hnd.dirname + ')]';
  
  require('dcp/node-libs/shutdown').atExit(removeFn);
  return hnd;
}

/** 
 * Securely create a temporary socket (eg for IPC) in the system temp directory. A cleanup is registered to
 * automatically remove the file when the process is closed.
 * 
 * @param    {string}   basenamePattern A string specifying the file basename pattern. The
 *                                      character X will be replaced with randomly-generated
 *                                      characters, similar to mkstemp(3).
 * @param    {string}   extension       [optional]  The filename extension to use; pass false for none
 * @param               perms           [optional]  The permissions argument to openSync. Default is 0600;
 *                                                  pass false to use OS default (0666 & umask(2)).
 * @param    {function} connectHandler  [optional]  The callback to invoke when the socket is connected to
 * @returns  {object} a socket, created with net.createConnection, using a path argument
 */
exports.createTempSocket = function utils$$createTempSocket(basenamePattern, extension, connectHandler, perms) {
  const inspect  = requireNative('util').inspect.custom;
  var pipeDir;
  
  if (os.platform() === 'win32')
    pipeDir = '\\\\.\\pipe\\';

  function createSocket(filename)
  {
    const net = requireNative('net');
    var   server = net.createServer(connectHandler);
    var   socket = server.listen(filename); /* creates the socket (named pipe) */

    socket.unref();

    return net.createConnection(filename);
  }

  const { thing: socket, removeFn, filename } = exports.createTempThing(createSocket, basenamePattern, extension, pipeDir);
  assert(os.platform() !== 'win32' || filename.startsWith('\\\\.\\pipe\\'));

  if (!socket)
    throw new Error(`Could not create socket ${filename || 'like ' + basenamePattern + '.' + extension}`); /* this should be impossible */

  if (filename && perms !== false) {
    try {
      fs.chmodSync(filename, perms || 0o600);
    } catch(error) {
      if (socket.close)
        socket.close();
      console.error(`Could not change permissions on named pipe ${filename} (${error.code || error.message}); closing`);
      throw error;
    }
  }

  socket[inspect] = () => '[Object utils$$tempSocket(' + filename + ')]';
  if (os.platform() !== 'win32') /* Pipe namespace automatically cleaned up on win32 */
  {
    socket.on('close', removeFn);  
    require('dcp/node-libs/shutdown').atExit(removeFn);
  }
  return socket;
}

/**
 * Copy the entire source file into the target, appending at the current
 * file offset.  If source file has been closed, we will re-open for the
 * the duration of this call in read-only mode. This routine does not attempt
 * to preserve the file position of the input file.
 *
 * @param   source   {object}     The file to copy, handle from createTempFile
 * @param   target   {object}     The file to append to, handle from createTempFile
 * @param   target2  {object}     An optioanl stream which also receives the same 
 *                                data via its write method. For npm create-hash.
 */  
exports.catFile = function utils$$catFile(source, target, target2) {
  var buf = Buffer.allocUnsafe(8192 * 64);
  var pos = 0;
  var nRead;
  var sourceFd = source.fd;
  var targetFd = target.fd;

  if (!sourceFd && sourceFd !== 0) {
    sourceFd = fs.openSync(source.filename, fs.O_RDONLY, 0o400);
  }
  
  do {
    nRead = fs.readSync(sourceFd, buf, 0, buf.length, pos);
    fs.writeSync(targetFd, buf.slice(0, nRead));
    pos += nRead;
    if (target2)
      target2.write(buf.slice(0, nRead));
  } while(nRead === buf.length);

  if (!source.fd && sourceFd !== 0) {
    fs.closeSync(sourceFd);
  }
}
